// @flow

import React from 'react';
import classNames from 'classnames';
import Box from 'sail/Box';
import Toggle, {ToggleItem} from 'sail/Toggle';
import Select from 'sail/Select';
import Group from 'sail/Group';
import {BodyAlt} from 'sail/Text';
import {defineMessages, FormattedMessage} from 'react-intl';
import {usePrefs} from 'docs/data';
import type {VideoPlaylistModel, VideoModel} from 'docs/data';

type PlaylistHeaderProps = {|
  playlist: VideoPlaylistModel,
  onChangeVideo: (video: VideoModel) => any,
  currentVideo: VideoModel,
|};

type PlaylistLanguageSelectorProps = {|
  options: any,
  onChangePrefs: (prefs: {lang: string, client: string}) => any,
|};

type SelectorProps = {|
  label: any,
  selectorType: string,
  activeOption: string,
  options: Array<string>,
  onChangePrefs: (option: string) => void,
|};

const messages = defineMessages({
  platform: {
    id: 'docsApp.videos.settings.platform',
    description: 'Platform label',
    defaultMessage: 'Platform:',
  },
  frontend: {
    id: 'docsApp.videos.settings.frontend',
    description: 'Frontend label',
    defaultMessage: 'Frontend:',
  },
  backend: {
    id: 'docsApp.videos.settings.backend',
    description: 'Backend label',
    defaultMessage: 'Backend:',
  },
});

const Selector = ({
  label,
  selectorType,
  activeOption,
  options,
  onChangePrefs,
}: SelectorProps) => {
  const {setPrefs} = usePrefs();

  return (
    <Box flex={{alignItems: 'center'}}>
      <BodyAlt color="dark" style={{marginRight: 12}}>
        {label}
      </BodyAlt>
      <Box className={classNames('Selector--full')}>
        <Toggle name={selectorType} type="radio">
          {options.map((option) => {
            return (
              <ToggleItem
                key={option}
                label={option}
                checked={option === activeOption}
                onChange={() => {
                  setPrefs({[selectorType]: option});
                  onChangePrefs(option);
                }}
              />
            );
          })}
        </Toggle>
      </Box>
      <Box className={classNames('Selector--small')}>
        <Select
          value={activeOption}
          options={options.map((option) => {
            return {
              label: option,
              value: option,
            };
          })}
          onChange={(option) => {
            setPrefs({lang: option});
            onChangePrefs(option);
          }}
        />
      </Box>
    </Box>
  );
};

const ClientSelector = ({
  options,
  onChangePrefs,
}: PlaylistLanguageSelectorProps) => {
  const {getPref} = usePrefs();

  const clientOptions = Object.keys(options);

  if (clientOptions.length > 0) {
    const clientPref = getPref('client');

    const clientLang =
      clientPref && options[clientPref] ? clientPref : 'javascript';

    return (
      <Selector
        label={<FormattedMessage {...messages.frontend} />}
        selectorType="client"
        activeOption={clientLang}
        options={clientOptions}
        onChangePrefs={(newLang) => {
          onChangePrefs({
            lang: getPref('lang') || 'ruby',
            client: newLang,
          });
        }}
      />
    );
  }
  return '';
};

const ServerSelector = ({
  options,
  onChangePrefs,
}: PlaylistLanguageSelectorProps) => {
  const {getPref} = usePrefs();

  const serverOptions = Object.keys(options);

  if (serverOptions.length > 0) {
    const serverPref = getPref('lang');

    const serverLang = serverPref && options[serverPref] ? serverPref : 'ruby';

    return (
      <Selector
        label={<FormattedMessage {...messages.backend} />}
        selectorType="lang"
        activeOption={serverLang}
        options={serverOptions}
        onChangePrefs={(newLang) => {
          onChangePrefs({
            lang: newLang,
            client: getPref('client') || 'javascript',
          });
        }}
      />
    );
  }

  return '';
};

export const PlaylistHeader = ({
  playlist,
  onChangeVideo,
  currentVideo,
}: PlaylistHeaderProps) => {
  const getNewVideo = (
    prefs: {lang: string, client: string},
    topicId: string,
  ): VideoModel => {
    const topicVideos = playlist.chapters.find((topic) => {
      return topic.id === topicId;
    });

    if (!topicVideos) {
      return currentVideo;
    }

    const prefMatchVideo = topicVideos.videos.find((video) => {
      return video.prefs.lang === prefs.lang;
    });

    if (prefMatchVideo) {
      return prefMatchVideo;
    }

    return topicVideos.defaultVideo;
  };

  const onChangePrefs = (prefs: {lang: string, client: string}) => {
    const topicId = currentVideo.id.substring(
      0,
      currentVideo.id.lastIndexOf('-'),
    );
    const newVideo = getNewVideo(prefs, topicId);
    onChangeVideo(newVideo);
  };

  return (
    <Group direction="horizontal" alignItems="center" spacing={32}>
      <ServerSelector
        options={playlist.prefs?.lang || []}
        onChangePrefs={onChangePrefs}
      />
      <ClientSelector
        options={playlist.prefs?.client || []}
        onChangePrefs={onChangePrefs}
      />
    </Group>
  );
};
